import { useState } from "react";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitFleetQuote } from "@/lib/api";

const SERVICE_OPTIONS = [
  "Defektjavítás",
  "Helyszíni gumicsere",
  "Szezonális csere",
  "Gumi tárolás",
  "Új/Használt gumi értékesítés",
];

export default function FleetForm() {
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    phone: "",
    email: "",
    fleet_size: "",
    services_needed: [],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleService = (s) => {
    setForm((f) => ({
      ...f,
      services_needed: f.services_needed.includes(s)
        ? f.services_needed.filter((x) => x !== s)
        : [...f.services_needed, s],
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitFleetQuote({
        ...form,
        fleet_size: parseInt(form.fleet_size, 10),
      });
      setSent(true);
      toast.success("Ajánlatkérés elküldve! Hamarosan keresünk.");
      setForm({
        company_name: "",
        contact_name: "",
        phone: "",
        email: "",
        fleet_size: "",
        services_needed: [],
        message: "",
      });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Hiba történt. Ellenőrizd az adatokat.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-gray-50 border border-gray-200 rounded-sm px-4 py-3.5 text-gray-900 placeholder:text-gray-300 focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-400 transition-all font-medium";
  const labelCls = "block text-xs uppercase tracking-[0.2em] text-gray-500 font-bold mb-2";

  return (
    <section id="flotta" data-testid="fleet-section" className="relative py-24 md:py-32">
      <div className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1587813369290-091c9d432daf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHx2YW4lMjBmbGVldHxlbnwwfHx8fDE3ODIzNzYzNjF8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover opacity-[0.08]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050508] via-[#050508]/85 to-[#050508]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="text-xs uppercase tracking-[0.3em] text-violet-600 font-bold mb-4">[ 05 ] · B2B / Flotta</div>
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl leading-[0.95]">
              Flotta<br />ajánlatkérő<br /><span className="text-gray-400">űrlap.</span>
            </h2>
            <p className="mt-6 text-gray-500 leading-relaxed max-w-md">
              Több járműved van? Futárszolgálatot, taxi flottát vagy céges autóparkot üzemeltetsz? Kérj egyedi keretszerződést kedvezményes árakkal, prioritásos kiszállással.
            </p>

            <div className="mt-10 space-y-4">
              {[
                "Egyedi nagykereskedelmi árazás",
                "Prioritásos kiszállás 24/7",
                "Havi gyűjtőszámla, átlátható elszámolás",
                "Dedikált flotta menedzser",
              ].map((b) => (
                <div key={b} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-violet-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-600">{b}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              data-testid="fleet-form"
              onSubmit={onSubmit}
              className="border border-gray-200 bg-gray-100/80 backdrop-blur-md p-6 md:p-10 rounded-sm noise-overlay"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Cégnév *</label>
                  <input
                    data-testid="fleet-input-company"
                    required
                    value={form.company_name}
                    onChange={(e) => update("company_name", e.target.value)}
                    className={inputCls}
                    placeholder="Pl. Acme Logisztika Kft."
                  />
                </div>
                <div>
                  <label className={labelCls}>Kapcsolattartó *</label>
                  <input
                    data-testid="fleet-input-contact"
                    required
                    value={form.contact_name}
                    onChange={(e) => update("contact_name", e.target.value)}
                    className={inputCls}
                    placeholder="Teljes név"
                  />
                </div>
                <div>
                  <label className={labelCls}>Telefon *</label>
                  <input
                    data-testid="fleet-input-phone"
                    required
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    className={inputCls}
                    placeholder="+36 30 123 4567"
                  />
                </div>
                <div>
                  <label className={labelCls}>E-mail *</label>
                  <input
                    data-testid="fleet-input-email"
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    className={inputCls}
                    placeholder="iroda@ceg.hu"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Járművek száma *</label>
                  <input
                    data-testid="fleet-input-size"
                    required
                    type="number"
                    min={1}
                    value={form.fleet_size}
                    onChange={(e) => update("fleet_size", e.target.value)}
                    className={inputCls}
                    placeholder="Pl. 12"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className={labelCls}>Szükséges szolgáltatások</label>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_OPTIONS.map((s) => {
                    const active = form.services_needed.includes(s);
                    return (
                      <button
                        type="button"
                        key={s}
                        data-testid={`fleet-service-${s.replace(/\s+/g, "-").toLowerCase()}`}
                        onClick={() => toggleService(s)}
                        className={`px-4 py-2 text-xs uppercase tracking-wider font-bold rounded-sm border transition-all ${
                          active
                            ? "bg-violet-500/20 border-violet-400 text-gray-900"
                            : "bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-400"
                        }`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-6">
                <label className={labelCls}>Üzenet</label>
                <textarea
                  data-testid="fleet-input-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  rows={4}
                  className={inputCls + " resize-none"}
                  placeholder="Mesélj a flottádról, vártálban a havi átszerelések számáról, igényeidről..."
                />
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-[11px] text-gray-400 leading-relaxed max-w-md">
                  Az űrlap elküldésével hozzájárulsz, hogy adataidat ajánlattétel céljából feldolgozzuk.
                </p>
                <button
                  data-testid="fleet-submit"
                  type="submit"
                  disabled={loading || sent}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 text-white font-bold uppercase tracking-wider text-sm rounded-sm transition-all glow-blue disabled:opacity-50 w-full sm:w-auto"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sent ? "Elküldve ✓" : loading ? "Küldés..." : "Ajánlatkérés küldése"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
